/*
 * WEEKLY CARDS ADMIN - EDIT LIVE PAGE
 *
 * NON-NEGOTIABLE UX PHILOSOPHY:
 * 1. This page edits the CURRENTLY-LIVE edition only
 * 2. Single action: [Save Changes] - updates live edition immediately
 * 3. Changes persist directly to live content (no draft intermediary)
 * 4. Unsaved changes warning when navigating away
 * 5. Artist Wisdom: image upload + quote + artist name
 * 6. Civic Moment: country selector (searchable, title = country name) + story + optional source (image from country)
 * 7. Spark a Thought: question + optional subtext
 */

import { useState, useEffect } from 'react';
import { Loader2, Upload, Search, ChevronDown } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import Toast from '../components/Toast';
import CalendarPicker from '../components/CalendarPicker';
import { useApp } from '../lib/AppContext';
import {
  getCurrentLiveEdition,
  upsertCard,
  getAllCountries,
  uploadWeeklyCardImage,
  deleteWeeklyCardImage,
  WeekWithCards,
  Country,
  CardType,
} from '../lib/weeklyCardsPlanner';

interface EditorFormData {
  artist_wisdom: {
    content: string;
    attribution: string;
    image_url: string;
  };
  civic_moment: {
    headline: string;
    content: string;
    country_id: string;
    event_date: string;
    source_name: string;
    source_url: string;
    story_image_url: string;
  };
  spark_a_thought: {
    content: string;
    subtext: string;
  };
}

function formatWeekLabel(weekOf: string): string {
  const date = new Date(weekOf + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminWeeklyCardsEditLive() {
  const { navigateTo } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<CardType | null>(null);
  const [liveWeek, setLiveWeek] = useState<WeekWithCards | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [formData, setFormData] = useState<EditorFormData>({
    artist_wisdom: { content: '', attribution: '', image_url: '' },
    civic_moment: { headline: '', content: '', country_id: '', event_date: '', source_name: '', source_url: '', story_image_url: '' },
    spark_a_thought: { content: '', subtext: '' },
  });

  const [originalFormData, setOriginalFormData] = useState<EditorFormData>({
    artist_wisdom: { content: '', attribution: '', image_url: '' },
    civic_moment: { headline: '', content: '', country_id: '', event_date: '', source_name: '', source_url: '', story_image_url: '' },
    spark_a_thought: { content: '', subtext: '' },
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  const isDirty = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, originalFormData]);

  async function loadData() {
    setLoading(true);
    const [live, allCountries] = await Promise.all([
      getCurrentLiveEdition(),
      getAllCountries(),
    ]);

    if (!live) {
      showToast('No active edition found', 'error');
      navigateTo('admin-weekly-cards');
      return;
    }

    setLiveWeek(live);
    setCountries(allCountries);
    loadWeekIntoForm(live);

    setLoading(false);
  }

  function loadWeekIntoForm(week: WeekWithCards) {
    const newFormData: EditorFormData = {
      artist_wisdom: { content: '', attribution: '', image_url: '' },
      civic_moment: { headline: '', content: '', country_id: '', event_date: '', source_name: '', source_url: '', story_image_url: '' },
      spark_a_thought: { content: '', subtext: '' },
    };

    week.cards.forEach(card => {
      if (card.card_type === 'artist_wisdom') {
        newFormData.artist_wisdom = {
          content: card.content,
          attribution: card.attribution || '',
          image_url: card.image_url || '',
        };
      } else if (card.card_type === 'civic_moment') {
        newFormData.civic_moment = {
          headline: card.headline || '',
          content: card.content,
          country_id: card.country_id || '',
          event_date: card.event_date || '',
          source_name: card.source_name || '',
          source_url: card.source_url || '',
          story_image_url: card.story_image_url || '',
        };
      } else if (card.card_type === 'spark_a_thought') {
        newFormData.spark_a_thought = {
          content: card.content,
          subtext: card.subtext || '',
        };
      }
    });

    setFormData(newFormData);
    setOriginalFormData(JSON.parse(JSON.stringify(newFormData)));
  }

  async function saveCards(isDraft: boolean = true) {
    if (!liveWeek) return;

    const cardTypes: CardType[] = ['artist_wisdom', 'civic_moment', 'spark_a_thought'];

    for (const cardType of cardTypes) {
      const data = formData[cardType];
      if (cardType === 'artist_wisdom') {
        const result = await upsertCard(liveWeek.week.id, {
          card_type: cardType,
          content: data.content,
          attribution: data.attribution,
          image_url: data.image_url || null,
          is_draft: isDraft,
        });
        console.log('[AdminWeeklyCardsEditLive] Saved artist_wisdom:', result);
      } else if (cardType === 'civic_moment') {
        const country = countries.find(c => c.id === data.country_id);
        const result = await upsertCard(liveWeek.week.id, {
          card_type: cardType,
          title: country?.name || '',
          headline: data.headline || null,
          content: data.content,
          country_id: data.country_id || null,
          event_date: data.event_date || null,
          source_name: data.source_name || null,
          source_url: data.source_url || null,
          story_image_url: data.story_image_url || null,
          is_draft: isDraft,
        });
        console.log('[AdminWeeklyCardsEditLive] Saved civic_moment:', result);
      } else {
        const result = await upsertCard(liveWeek.week.id, {
          card_type: cardType,
          content: data.content,
          subtext: data.subtext || null,
          is_draft: isDraft,
        });
        console.log('[AdminWeeklyCardsEditLive] Saved spark_a_thought:', result);
      }
    }
  }

  async function handleSaveChanges() {
    if (!liveWeek) return;

    console.log('[AdminWeeklyCardsEditLive] Saving changes. Form data:', formData);
    setSaving(true);
    try {
      await saveCards(false);
      // Reload data from database to ensure we show what was actually saved
      await loadData();
      console.log('[AdminWeeklyCardsEditLive] Changes saved successfully. Reloaded data.');
      showToast('Changes saved', 'success');
    } catch (err) {
      console.error('[AdminWeeklyCardsEditLive] Error saving changes:', err);
      showToast('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (isDirty()) {
      setShowDiscardModal(true);
    } else {
      navigateTo('admin-weekly-cards');
    }
  }

  function handleDiscardChanges() {
    // Discard changes and navigate to overview
    setShowDiscardModal(false);
    navigateTo('admin-weekly-cards');
  }

  async function handleImageUpload(cardType: CardType, file: File) {
    if (!liveWeek) return;

    setUploading(cardType);
    try {
      const imageUrl = await uploadWeeklyCardImage(liveWeek.week.id, cardType, file);

      if (imageUrl) {
        if (cardType === 'artist_wisdom') {
          setFormData(prev => ({
            ...prev,
            artist_wisdom: { ...prev.artist_wisdom, image_url: imageUrl },
          }));
        }
        showToast('Image uploaded successfully', 'success');
      } else {
        showToast('Failed to upload image', 'error');
      }
    } catch (err) {
      console.error('[AdminWeeklyCardsEditLive] Error uploading image:', err);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploading(null);
    }
  }

  async function handleImageRemove(cardType: CardType) {
    if (!liveWeek) return;

    try {
      await deleteWeeklyCardImage(liveWeek.week.id, cardType);
      if (cardType === 'artist_wisdom') {
        setFormData(prev => ({
          ...prev,
          artist_wisdom: { ...prev.artist_wisdom, image_url: '' },
        }));
      }
      showToast('Image removed', 'success');
    } catch (err) {
      console.error('[AdminWeeklyCardsEditLive] Error removing image:', err);
      showToast('Failed to remove image', 'error');
    }
  }

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  }

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const selectedCountry = countries.find(c => c.id === formData.civic_moment.country_id);

  if (loading) {
    return (
      <AdminShell>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-[#40E0D0] animate-spin" />
        </div>
      </AdminShell>
    );
  }

  if (!liveWeek) {
    return (
      <AdminShell>
        <div className="w-full max-w-[1120px] py-12 px-8">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-12 text-center">
            <p className="text-[14px] text-[#808080] mb-4">No active edition found</p>
            <button
              onClick={() => navigateTo('admin-weekly-cards')}
              className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all"
            >
              Back to Overview
            </button>
          </div>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div className="p-8 w-full max-w-[1120px]">
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className="text-[14px] text-[#40E0D0] hover:text-[#47EAD8] transition-colors mb-4"
          >
            ← Back to overview
          </button>
          <h1 className="text-[28px] font-semibold text-[#F5F5F5] mb-2">Edit Active Edition</h1>
          <p className="text-[14px] text-[#808080]">
            Week of {formatWeekLabel(liveWeek.week.week_of)}
          </p>
          <p className="text-[13px] text-[#606060] mt-2">
            Changes will update the currently active weekly cards immediately
          </p>
        </div>

        <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-8 mb-6">
          <div className="space-y-8">
            <div>
              <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4">Artist Wisdom</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Quote <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.artist_wisdom.content}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        artist_wisdom: { ...prev.artist_wisdom, content: e.target.value }
                      }));
                    }}
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0] resize-none"
                    placeholder="Enter the artist's quote..."
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Artist Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.artist_wisdom.attribution}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        artist_wisdom: { ...prev.artist_wisdom, attribution: e.target.value }
                      }));
                    }}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="Artist name"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Artist Image
                  </label>
                  {formData.artist_wisdom.image_url ? (
                    <div className="flex items-start gap-4">
                      <img
                        src={formData.artist_wisdom.image_url}
                        alt="Artist"
                        className="w-32 h-32 rounded-lg object-cover border border-[#282828]"
                      />
                      <div className="flex flex-col gap-2">
                        <label className="block">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload('artist_wisdom', file);
                            }}
                            className="hidden"
                          />
                          <span className="px-4 py-2 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[13px] font-medium transition-colors cursor-pointer inline-block">
                            Replace Image
                          </span>
                        </label>
                        <button
                          onClick={() => handleImageRemove('artist_wisdom')}
                          className="px-4 py-2 text-[13px] text-red-400 hover:text-red-300 transition-colors text-left"
                        >
                          Remove Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload('artist_wisdom', file);
                        }}
                        className="hidden"
                      />
                      <div className="flex items-center justify-center px-4 py-8 border-2 border-dashed border-[#282828] rounded-lg cursor-pointer hover:border-[#40E0D0] transition-colors">
                        {uploading === 'artist_wisdom' ? (
                          <Loader2 className="w-5 h-5 text-[#40E0D0] animate-spin" />
                        ) : (
                          <div className="text-center">
                            <Upload className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                            <span className="text-[13px] text-[#808080]">Click to upload artist image</span>
                          </div>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-[#282828] pt-8">
              <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4">Civic Moment</h3>
              <div className="space-y-4">
                {/* Headline */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[13px] font-medium text-[#A0A0A0]">
                      Headline <span className="text-red-400">*</span>
                    </label>
                    <span className={`text-[12px] ${
                      formData.civic_moment.headline.length > 50
                        ? 'text-red-400'
                        : 'text-[#606060]'
                    }`}>
                      {formData.civic_moment.headline.length} / 50 chars
                    </span>
                  </div>
                  <input
                    type="text"
                    value={formData.civic_moment.headline}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, headline: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="Short story headline"
                  />
                  {formData.civic_moment.headline.length > 50 && (
                    <p className="text-[12px] text-red-400 mt-1.5">
                      Headline exceeds 50 characters. Please shorten.
                    </p>
                  )}
                </div>

                {/* Story Image */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Story Image <span className="text-red-400">*</span>
                  </label>
                  {formData.civic_moment.story_image_url ? (
                    <div className="flex items-start gap-4">
                      <img
                        src={formData.civic_moment.story_image_url}
                        alt="Story"
                        className="w-32 h-32 rounded-lg object-cover border border-[#282828]"
                      />
                      <div className="flex flex-col gap-2">
                        <label className="block">
                          <input
                            id="civic-image-upload-live"
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file || !liveWeek) return;
                              setUploading('civic_moment');
                              try {
                                const url = await uploadWeeklyCardImage(liveWeek.week.id, 'civic_moment', file);
                                if (url) {
                                  setFormData(prev => ({
                                    ...prev,
                                    civic_moment: { ...prev.civic_moment, story_image_url: url }
                                  }));
                                } else {
                                  showToast('Image upload failed', 'error');
                                }
                              } catch (error) {
                                console.error('Upload failed:', error);
                                showToast('Image upload failed', 'error');
                              } finally {
                                setUploading(null);
                                e.target.value = '';
                              }
                            }}
                            className="hidden"
                          />
                          <span className="px-4 py-2 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[13px] font-medium transition-colors cursor-pointer inline-block">
                            Replace Image
                          </span>
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              civic_moment: { ...prev.civic_moment, story_image_url: '' }
                            }));
                          }}
                          className="px-4 py-2 text-[13px] text-red-400 hover:text-red-300 transition-colors text-left"
                        >
                          Remove Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload('civic_moment', file);
                        }}
                        className="hidden"
                      />
                      <div className="flex items-center justify-center px-4 py-8 border-2 border-dashed border-[#282828] rounded-lg cursor-pointer hover:border-[#40E0D0] transition-colors">
                        {uploading === 'civic_moment' ? (
                          <Loader2 className="w-5 h-5 text-[#40E0D0] animate-spin" />
                        ) : (
                          <div className="text-center">
                            <Upload className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                            <span className="text-[13px] text-[#808080]">Click to upload story image</span>
                          </div>
                        )}
                      </div>
                    </label>
                  )}
                </div>

                {/* Story */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[13px] font-medium text-[#A0A0A0]">
                      Story <span className="text-red-400">*</span>
                    </label>
                    <span className={`text-[12px] ${
                      formData.civic_moment.content.length > 200
                        ? 'text-red-400'
                        : 'text-[#606060]'
                    }`}>
                      {formData.civic_moment.content.length} / 200 chars
                    </span>
                  </div>
                  <textarea
                    value={formData.civic_moment.content}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, content: e.target.value }
                    }))}
                    rows={4}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0] resize-none"
                    placeholder="Describe the civic moment..."
                  />
                  {formData.civic_moment.content.length > 200 && (
                    <p className="text-[12px] text-red-400 mt-1.5">
                      Story exceeds 200 characters. Please shorten.
                    </p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Country <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-left text-[14px] hover:border-[#40E0D0] transition-colors flex items-center justify-between"
                    >
                      <span className={selectedCountry ? 'text-[#F5F5F5]' : 'text-[#606060]'}>
                        {selectedCountry ? `${selectedCountry.name} [${selectedCountry.iso3}]` : 'Select country'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-[#808080]" />
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute z-50 w-full mt-2 bg-[#1A1A1A] border border-[#282828] rounded-lg shadow-xl max-h-64 overflow-hidden">
                        <div className="p-3 border-b border-[#282828]">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#606060]" />
                            <input
                              type="text"
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              placeholder="Search countries..."
                              className="w-full pl-10 pr-4 py-2 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-80">
                          {filteredCountries.map(country => (
                            <button
                              key={country.id}
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  civic_moment: { ...prev.civic_moment, country_id: country.id }
                                }));
                                setShowCountryDropdown(false);
                                setCountrySearch('');
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-[#282828] transition-colors text-[14px] text-[#F5F5F5] border-b border-[#282828] last:border-b-0"
                            >
                              {country.name} [{country.iso3}]
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Date <span className="text-red-400">*</span>
                  </label>
                  <CalendarPicker
                    value={formData.civic_moment.event_date}
                    onChange={(date) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, event_date: date }
                    }))}
                    placeholder="Select event date"
                  />
                </div>

                {/* Source Name */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Source Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.civic_moment.source_name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, source_name: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="e.g., NPR, Reuters"
                  />
                </div>

                {/* Source Link */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Source Link <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.civic_moment.source_url}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, source_url: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-[#282828] pt-8">
              <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4">Spark a Thought</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    A question that encourages users to reflect on during the week. <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.spark_a_thought.content}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        spark_a_thought: { ...prev.spark_a_thought, content: e.target.value }
                      }));
                    }}
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0] resize-none"
                    placeholder="Enter a thought-provoking question or reflection..."
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Subtext (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.spark_a_thought.subtext}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        spark_a_thought: { ...prev.spark_a_thought, subtext: e.target.value }
                      }));
                    }}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="Optional additional context"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-6 bg-[#0A0A0A] border border-[#282828] rounded-xl p-4 flex items-center justify-between shadow-2xl">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2.5 text-[#808080] hover:text-[#F5F5F5] text-[14px] font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={saving || !isDirty()}
            className="px-6 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}

      {showDiscardModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">Discard changes?</h2>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              You have unsaved changes. Are you sure you want to leave?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDiscardModal(false)}
                className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all"
              >
                Continue editing
              </button>
              <button
                onClick={handleDiscardChanges}
                className="px-5 py-2.5 bg-transparent hover:bg-[#282828] text-red-400 hover:text-red-300 border border-red-400/30 hover:border-red-400/50 rounded-lg text-[14px] font-medium transition-all"
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
